import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { FormGroup } from '@angular/forms';

import { EMPTY, Observable, Subject, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { RelatedToType } from 'app/shared/enums';
import { ImportResponse, ImportField } from './imports.types';
import { Dictionary } from 'app/shared/models/dictionary';

@Injectable({
  providedIn: 'root',
})
export class ImportsService {
  public readonly getErrorSubject$ = new Subject<HttpErrorResponse>();
  public readonly importStatusErrorSubject$ = new Subject<HttpErrorResponse>();
  public readonly deleteErrorSubject$ = new Subject<HttpErrorResponse>();

  constructor(private readonly _httpClient: HttpClient) {}

  public get$(
    accountId: number,
    page?: number,
    pageSize?: number,
  ): Observable<{ imports: ImportResponse[]; total: number }> {
    const url =
      page && pageSize
        ? `accounts/${accountId}/imports?page=${page}&per_page=${pageSize}`
        : `accounts/${accountId}/imports`;
    return this._httpClient.get<{ imports: ImportResponse[]; total: number }>(url).pipe(
      take(1),
      map(({ imports, total }) => ({ imports, total })),
      catchError((errorResponse: HttpErrorResponse) => {
        this.getErrorSubject$.next(errorResponse);
        return EMPTY;
      }),
    );
  }

  public getImportById$(accountId: number, importId: number): Observable<ImportResponse> {
    return this._httpClient.get<{ import: ImportResponse }>(`accounts/${accountId}/imports/${importId}`).pipe(
      take(1),
      map((response) => response.import),
      catchError((errorResponse: HttpErrorResponse) => {
        this.importStatusErrorSubject$.next(errorResponse);
        return EMPTY;
      }),
    );
  }

  public add$(accountId: number, form: FormGroup): Observable<ImportResponse> {
    const body = new FormData();
    body.append('file', form.get('uploaded_file').value);
    body.append('related_to_type', form.get('object_type').value);
    body.append('mode', form.get('mode').value.value);

    return this._httpClient.post<{ import: ImportResponse }>(`accounts/${accountId}/imports`, body).pipe(
      take(1),
      map((response) => response.import),
      catchError((error: HttpErrorResponse) => throwError(() => error)),
    );
  }

  public update$(
    accountId: number,
    importId: number,
    mapping?: Dictionary<string>,
    form?: FormGroup,
    isSameFile?: boolean,
  ): Observable<ImportResponse> {
    const body = new FormData();
    if (mapping) body.append('mapping', JSON.stringify(mapping));
    if (form) {
      if (!isSameFile) body.append('file', form.get('uploaded_file').value);
      body.append('mode', form.get('mode').value.value || form.get('mode').value);
    }

    return this._httpClient.put<{ import: ImportResponse }>(`accounts/${accountId}/imports/${importId}`, body).pipe(
      take(1),
      map((response) => response.import),
      catchError((error: HttpErrorResponse) => throwError(() => error)),
    );
  }

  public delete$(accountId: number, importId: number): Observable<void> {
    return this._httpClient.delete<void>(`accounts/${accountId}/imports/${importId}`).pipe(
      take(1),
      catchError((errorResponse: HttpErrorResponse) => {
        this.deleteErrorSubject$.next(errorResponse);
        return EMPTY;
      }),
    );
  }

  public getFields$(accountId: number, relatedToType: RelatedToType): Observable<ImportField[]> {
    return this._httpClient
      .get<{ import_fields: ImportField[] }>(`accounts/${accountId}/imports/fields?related_to_type=${relatedToType}`)
      .pipe(
        take(1),
        map(({ import_fields }) => import_fields),
        catchError((error: HttpErrorResponse) => throwError(() => error)),
      );
  }

  public run$(accountId: number, importId: number): Observable<ImportResponse> {
    return this._httpClient
      .post<{ import: ImportResponse }>(`accounts/${accountId}/imports/${importId}/run`, null)
      .pipe(
        take(1),
        map((response) => response.import),
        catchError((error: HttpErrorResponse) => throwError(() => error)),
      );
  }

  public getExportableFieldsFile$(accountId: number, relatedToType: RelatedToType): Observable<string> {
    return this._httpClient
      .get<{ data: string }>(`accounts/${accountId}/imports/template?related_to_type=${relatedToType}`)
      .pipe(
        take(1),
        map(({ data }) => data),
        catchError((error: HttpErrorResponse) => throwError(() => error)),
      );
  }
}
